import React, { useState } from "react";
import { 
  Share2, Copy, Check, MessageCircle, 
  Facebook, Twitter, Send, Linkedin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Instagram icon
const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

// TikTok icon
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

// Reddit icon
const RedditIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
  </svg>
);

// X (Twitter) icon - updated design
const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

export default function ShareButtons({ 
  url, 
  title, 
  description,
  type = "question",
  showLabel = true,
  size = "sm"
}) {
  const [copied, setCopied] = useState(false);

  const shareUrl = url || window.location.href;
  const shareTitle = title || "Check this out on AskIncog!";
  const shareText = description || shareTitle;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareToFacebook = () => {
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(fbUrl, '_blank', 'width=600,height=400');
  };

  const shareToTwitter = () => {
    const tweetUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(tweetUrl, '_blank', 'width=600,height=400');
  };

  const shareToLinkedIn = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=400');
  };

  const shareToWhatsApp = () => {
    const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
    window.open(waUrl, '_blank');
  };

  const shareToTelegram = () => {
    const tgUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(tgUrl, '_blank');
  };

  const shareToReddit = () => {
    const redditUrl = `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`;
    window.open(redditUrl, '_blank', 'width=600,height=400');
  };

  const shareToInstagram = () => {
    copyLink();
    alert("Link copied! 📋\n\nOpen Instagram, create a Story or Post, and paste the link in your bio or use 'Link' sticker.");
  };

  const shareToTikTok = () => {
    copyLink();
    alert("Link copied! 📋\n\nOpen TikTok, create a video about this topic, and paste the link in your bio or comments.");
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (e) {
        // User cancelled or share failed - silently ignore
        console.debug("Share cancelled:", e);
      }
    }
  };

  const hasNativeShare = typeof navigator !== 'undefined' && navigator.share;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size={size}
          className="rounded-full border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
        >
          <Share2 className="h-4 w-4" />
          {showLabel && <span className="ml-1.5">Share</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 border-slate-200 bg-white shadow-lg">
        {/* Copy Link */}
        <DropdownMenuItem onClick={copyLink} className="cursor-pointer hover:bg-slate-50">
          {copied ? <Check className="mr-2 h-4 w-4 text-green-500" /> : <Copy className="mr-2 h-4 w-4 text-slate-500" />}
          {copied ? "Copied!" : "Copy Link"}
        </DropdownMenuItem>

        {hasNativeShare && (
          <DropdownMenuItem onClick={nativeShare} className="cursor-pointer hover:bg-slate-50">
            <Share2 className="mr-2 h-4 w-4 text-slate-500" />
            More Options...
          </DropdownMenuItem>
        )}

        <div className="my-1 border-t border-slate-100" />

        {/* Facebook - Priority */}
        <DropdownMenuItem onClick={shareToFacebook} className="cursor-pointer hover:bg-blue-50">
          <Facebook className="mr-2 h-4 w-4 text-[#1877F2]" />
          Facebook
        </DropdownMenuItem>

        {/* Twitter/X - Priority */}
        <DropdownMenuItem onClick={shareToTwitter} className="cursor-pointer hover:bg-slate-50">
          <XIcon className="mr-2 h-4 w-4 text-slate-700" />
          X (Twitter)
        </DropdownMenuItem>

        {/* LinkedIn - Priority */}
        <DropdownMenuItem onClick={shareToLinkedIn} className="cursor-pointer hover:bg-blue-50">
          <Linkedin className="mr-2 h-4 w-4 text-[#0A66C2]" />
          LinkedIn
        </DropdownMenuItem>

        {/* Instagram - Priority */}
        <DropdownMenuItem onClick={shareToInstagram} className="cursor-pointer hover:bg-pink-50">
          <InstagramIcon className="mr-2 h-4 w-4 text-[#E4405F]" />
          Instagram
        </DropdownMenuItem>

        <div className="my-1 border-t border-slate-100" />

        {/* WhatsApp */}
        <DropdownMenuItem onClick={shareToWhatsApp} className="cursor-pointer hover:bg-green-50">
          <MessageCircle className="mr-2 h-4 w-4 text-[#25D366]" />
          WhatsApp
        </DropdownMenuItem>

        {/* Reddit */}
        <DropdownMenuItem onClick={shareToReddit} className="cursor-pointer hover:bg-orange-50">
          <RedditIcon className="mr-2 h-4 w-4 text-[#FF4500]" />
          Reddit
        </DropdownMenuItem>

        {/* TikTok */}
        <DropdownMenuItem onClick={shareToTikTok} className="cursor-pointer hover:bg-slate-50">
          <TikTokIcon className="mr-2 h-4 w-4 text-slate-700" />
          TikTok
        </DropdownMenuItem>

        {/* Telegram */}
        <DropdownMenuItem onClick={shareToTelegram} className="cursor-pointer hover:bg-cyan-50">
          <Send className="mr-2 h-4 w-4 text-[#0088cc]" />
          Telegram
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
